/**
 * Locale Context
 *
 * Single source of truth for the current locale.
 *
 * Resolution order (highest priority first):
 *   1. Organization's `locale` column (if user belongs to an org and a locale is set)
 *   2. Profile's `locale` column (DB)
 *   3. localStorage cache (`app_locale`)
 *   4. Auto-detect: if user's profile country = "US", default to "en-US"
 *   5. Browser navigator.language (e.g. "en-US", "de-AT")
 *   6. Hard fallback: "en"
 *
 * Supported locales: en, en-US, de, fr, es, it, pt
 *   (en-US falls back to en for any missing key — see src/lib/i18n.ts)
 */

import React, {
  createContext, useContext, useState, ReactNode, useCallback, useEffect,
} from "react";
import { supabase } from "@/integrations/supabase/client";

interface LocaleContextValue {
  locale: string;
  setLocale: (locale: string) => void;
}

const LocaleContext = createContext<LocaleContextValue | undefined>(undefined);

const DEFAULT_LOCALE = "en";
const LOCALE_STORAGE_KEY = "app_locale";

const SUPPORTED_LOCALES = ["en", "en-US", "de", "fr", "es", "it", "pt"];

/**
 * Normalize a raw locale string (e.g. "en_US", "EN-us", "de-AT") to a supported one.
 * Returns null if not supported.
 */
function normalizeLocale(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const cleaned = raw.replace("_", "-");

  // Exact match (case-insensitive on the language, case-insensitive on region)
  const supportedExact = SUPPORTED_LOCALES.find(
    (s) => s.toLowerCase() === cleaned.toLowerCase()
  );
  if (supportedExact) return supportedExact;

  // English variants: en-US stays en-US; everything else en-XX → en
  const lower = cleaned.toLowerCase();
  if (lower === "en-us") return "en-US";
  if (lower.startsWith("en")) return "en";

  // Other languages: take prefix
  const prefix = lower.split("-")[0];
  if (SUPPORTED_LOCALES.includes(prefix)) return prefix;
  return null;
}

function detectBrowserLocale(): string {
  if (typeof window === "undefined") return DEFAULT_LOCALE;
  const lang = navigator.language || (navigator as any).userLanguage || "";
  return normalizeLocale(lang) ?? DEFAULT_LOCALE;
}

function getInitialLocale(initialLocale?: string): string {
  if (initialLocale) return normalizeLocale(initialLocale) ?? DEFAULT_LOCALE;
  if (typeof window !== "undefined") {
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
    const normalized = normalizeLocale(stored);
    if (normalized) return normalized;
  }
  return detectBrowserLocale();
}

interface LocaleProviderProps {
  children: ReactNode;
  initialLocale?: string;
}

export function LocaleProvider({ children, initialLocale }: LocaleProviderProps) {
  const [locale, setLocaleState] = useState<string>(() => getInitialLocale(initialLocale));

  /** Persist locale to localStorage and (fire-and-forget) to DB profile */
  const setLocale = useCallback((newLocale: string) => {
    const normalized = normalizeLocale(newLocale) ?? DEFAULT_LOCALE;
    setLocaleState(normalized);
    if (typeof window !== "undefined") {
      localStorage.setItem(LOCALE_STORAGE_KEY, normalized);
    }
    // Fire-and-forget: update profile locale in DB
    supabase.auth.getUser().then(({ data }) => {
      if (data?.user?.id) {
        supabase
          .from("profiles")
          .update({ locale: normalized })
          .eq("id", data.user.id)
          .then(() => {/* intentionally silent */});
      }
    });
  }, []);

  // On mount: resolve final locale from DB (org → profile → US-country) and override local state
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: userData } = await supabase.auth.getUser();
      if (cancelled || !userData?.user?.id) return;

      const { data: profile } = await supabase
        .from("profiles")
        .select("locale, country")
        .eq("id", userData.user.id)
        .maybeSingle();
      if (cancelled) return;

      // 1. Org-level locale beats everything
      const { data: orgRoles } = await supabase
        .from("user_organization_roles")
        .select("organization_id")
        .eq("user_id", userData.user.id)
        .limit(1);
      if (cancelled) return;

      let resolved: string | null = null;

      if (orgRoles && orgRoles.length > 0) {
        const { data: org } = await supabase
          .from("organizations")
          .select("locale")
          .eq("id", orgRoles[0].organization_id)
          .maybeSingle();
        if (cancelled) return;
        resolved = normalizeLocale((org as any)?.locale);
      }

      // 2. Profile locale
      if (!resolved) resolved = normalizeLocale((profile as any)?.locale);

      // 4. US country auto-detect (only if no explicit profile locale was set)
      if (!resolved) {
        const country = ((profile as any)?.country as string | null)?.toUpperCase();
        if (country === "US" || country === "USA") resolved = "en-US";
      }

      if (resolved && resolved !== locale) {
        setLocaleState(resolved);
        localStorage.setItem(LOCALE_STORAGE_KEY, resolved);
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // run once on mount

  return (
    <LocaleContext.Provider value={{ locale, setLocale }}>
      {children}
    </LocaleContext.Provider>
  );
}

export function useLocale(): LocaleContextValue {
  const context = useContext(LocaleContext);
  if (!context) {
    throw new Error("useLocale must be used within a LocaleProvider");
  }
  return context;
}

/** Returns just the locale string — safe fallback if used outside provider */
export function useCurrentLocale(): string {
  const context = useContext(LocaleContext);
  return context?.locale ?? DEFAULT_LOCALE;
}

/** Exported for the org settings region dropdown */
export const SUPPORTED_LOCALE_OPTIONS = [
  { value: "en", label: "English (International)" },
  { value: "en-US", label: "English (United States)" },
  { value: "de", label: "Deutsch" },
  { value: "fr", label: "Français" },
  { value: "es", label: "Español" },
  { value: "it", label: "Italiano" },
  { value: "pt", label: "Português" },
] as const;
